/* ============================================================
   MOODSCENT — THEME.JS
   GSAP animations, interactions, navigation
   ============================================================ */

(function () {
  'use strict';

  // ============================================================
  // 1. GSAP INIT — attendre que GSAP soit chargé
  // ============================================================
  function initGSAP() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    document.documentElement.classList.add('gsap-ready');

    initScrollReveals();
    initParallax();
    initPageTransition();
    initSpotlight();
    initMagneticButtons();
    initGalleryParallax();
  }

  // ============================================================
  // 2. SCROLL REVEALS (blur + translate — Editorial Luxury)
  // ============================================================
  function initScrollReveals() {
    // Éléments individuels
    gsap.utils.toArray('[data-reveal]').forEach(function (el) {
      gsap.fromTo(
        el,
        { opacity: 0, y: 36, filter: 'blur(5px)' },
        {
          opacity: 1,
          y: 0,
          filter: 'blur(0px)',
          duration: 0.9,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 88%',
            once: true,
          },
        }
      );
    });

    // Groupes avec stagger
    gsap.utils.toArray('[data-reveal-stagger]').forEach(function (container) {
      var children = Array.from(container.children);
      if (!children.length) return;

      gsap.fromTo(
        children,
        { opacity: 0, y: 28, filter: 'blur(4px)' },
        {
          opacity: 1,
          y: 0,
          filter: 'blur(0px)',
          duration: 0.8,
          ease: 'power3.out',
          stagger: 0.1,
          scrollTrigger: {
            trigger: container,
            start: 'top 85%',
            once: true,
          },
        }
      );
    });

    // Héro content — entrée immédiate
    var heroContent = document.querySelector('.home-hero__content');
    if (heroContent) {
      gsap.from(heroContent.children, {
        opacity: 0,
        y: 30,
        duration: 1.1,
        ease: 'power3.out',
        stagger: 0.18,
        delay: 0.2,
      });
    }

    // Scroll indicator
    var heroScroll = document.querySelector('.home-hero__scroll');
    if (heroScroll) {
      gsap.from(heroScroll, {
        opacity: 0,
        duration: 1,
        delay: 1.2,
        ease: 'power2.out',
      });
    }
  }

  // ============================================================
  // 3. PARALLAX HERO
  // ============================================================
  function initParallax() {
    var heroBg = document.querySelector('.home-hero__bg');
    if (!heroBg) return;

    gsap.to(heroBg, {
      yPercent: -20,
      ease: 'none',
      scrollTrigger: {
        trigger: '.home-hero',
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });
  }

  // ============================================================
  // 4. HEADER — transparent → opaque au scroll
  // ============================================================
  function initHeader() {
    var header = document.querySelector('.site-header');
    if (!header) return;

    var hasHero = document.querySelector('.home-hero');
    if (!hasHero) {
      header.classList.add('is-floating');
      return;
    }

    var scrollThreshold = 80;
    var rafPending = false;

    function onScroll() {
      if (window.scrollY > scrollThreshold) {
        header.classList.add('scrolled');
        header.classList.add('is-floating');
      } else {
        header.classList.remove('scrolled');
        header.classList.remove('is-floating');
      }
    }

    window.addEventListener('scroll', function () {
      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(function () {
          onScroll();
          rafPending = false;
        });
      }
    }, { passive: true });
    onScroll();
  }

  // ============================================================
  // 5. MOBILE MENU
  // ============================================================
  function initMobileMenu() {
    var hamburger = document.querySelector('.hamburger');
    var mobileMenu = document.querySelector('.mobile-menu');
    var mobileClose = document.querySelector('.mobile-menu__close');

    if (!hamburger || !mobileMenu) return;

    function openMenu() {
      mobileMenu.classList.add('is-open');
      hamburger.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    }

    function closeMenu() {
      mobileMenu.classList.remove('is-open');
      hamburger.classList.remove('is-active');
      document.body.style.overflow = '';
    }

    hamburger.addEventListener('click', function () {
      if (mobileMenu.classList.contains('is-open')) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    if (mobileClose) {
      mobileClose.addEventListener('click', closeMenu);
    }

    // Fermer sur ESC
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeMenu();
    });

    // Fermer en cliquant sur un lien
    var mobileLinks = mobileMenu.querySelectorAll('.mobile-menu__link, .mobile-menu__sub-link');
    mobileLinks.forEach(function (link) {
      link.addEventListener('click', closeMenu);
    });
  }

  // ============================================================
  // 6. PRODUCT GALLERY — thumbnails
  // ============================================================
  function initProductGallery() {
    var gallery = document.querySelector('.product-gallery');
    if (!gallery) return;

    var mainWrap = gallery.querySelector('.product-gallery__main');
    var thumbs = gallery.querySelectorAll('.product-gallery__thumb');

    function swapMainImage(newSrc) {
      var existing = mainWrap.querySelector('img');
      if (!existing) return;
      // Neutraliser srcset/sizes (Shopify les génère, le navigateur les priorise sur src)
      existing.removeAttribute('srcset');
      existing.removeAttribute('sizes');
      existing.src = newSrc;
      existing.style.opacity = '0';
      if (typeof gsap !== 'undefined') {
        gsap.to(existing, { opacity: 1, duration: 0.35, ease: 'power2.out' });
      } else {
        existing.style.opacity = '1';
      }
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var newSrc = thumb.dataset.src;
        if (!newSrc || !mainWrap) return;

        // Fade out l'image actuelle puis swap
        var currentImg = mainWrap.querySelector('img');
        if (currentImg && typeof gsap !== 'undefined') {
          gsap.to(currentImg, {
            opacity: 0,
            duration: 0.18,
            onComplete: function () { swapMainImage(newSrc); },
          });
        } else {
          swapMainImage(newSrc);
        }

        // Activer le thumb
        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });

      // Aussi au clavier (accessibilité)
      thumb.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          thumb.click();
        }
      });
    });
  }

  // ============================================================
  // 7. STICKY ADD TO CART — mobile
  // ============================================================
  function initStickyAtc() {
    var stickyAtc = document.querySelector('.sticky-atc');
    var productAtcBtn = document.querySelector('.product-info__atc .btn');
    if (!stickyAtc || !productAtcBtn) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            stickyAtc.style.opacity = '0';
            stickyAtc.style.pointerEvents = 'none';
          } else {
            stickyAtc.style.opacity = '1';
            stickyAtc.style.pointerEvents = 'auto';
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(productAtcBtn);

    // Transition CSS
    stickyAtc.style.transition = 'opacity 0.3s ease';
  }

  // ============================================================
  // 8. PAGE TRANSITIONS
  // ============================================================
  function initPageTransition() {
    // Fade in à l'entrée de page
    document.body.style.opacity = '0';
    gsap.to(document.body, {
      opacity: 1,
      duration: 0.5,
      ease: 'power2.out',
    });

    // Fade out sur navigation
    document.querySelectorAll('a[href]').forEach(function (link) {
      // Exclure les liens externes, anchors, cart, etc.
      var href = link.getAttribute('href');
      if (
        !href ||
        href.startsWith('#') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        href.includes('cdn.shopify') ||
        link.hasAttribute('data-no-transition')
      ) {
        return;
      }

      link.addEventListener('click', function (e) {
        var target = link.getAttribute('target');
        if (target === '_blank') return;

        e.preventDefault();
        var url = link.href;

        gsap.to(document.body, {
          opacity: 0,
          duration: 0.3,
          ease: 'power2.in',
          onComplete: function () {
            window.location.href = url;
          },
        });
      });
    });
  }

  // ============================================================
  // 9. NEWSLETTER FORM
  // ============================================================
  function initNewsletter() {
    var forms = document.querySelectorAll('.footer-newsletter__form');
    forms.forEach(function (form) {
      form.addEventListener('submit', function () {
        // Shopify gère le submit via action, laisser passer
        var btn = form.querySelector('.footer-newsletter__btn');
        if (btn) {
          btn.textContent = '...';
          btn.disabled = true;
        }
      });
    });
  }

  // ============================================================
  // 10. CART COUNT — mise à jour dynamique
  // ============================================================
  function updateCartCount() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var counts = document.querySelectorAll('.cart-count');
        counts.forEach(function (el) {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? 'flex' : 'none';
        });
      })
      .catch(function () {});
  }

  // ============================================================
  // 11. ADD TO CART — AJAX
  // ============================================================
  function initAddToCart() {
    var forms = document.querySelectorAll('[data-product-form]');
    forms.forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();

        var btn = form.querySelector('[data-atc-btn]');
        var originalText = btn ? btn.textContent : '';

        if (btn) {
          btn.textContent = 'Ajout...';
          btn.disabled = true;
        }

        var formData = new FormData(form);
        formData.append('sections', 'cart-notification');

        fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        })
          .then(function (r) { return r.json(); })
          .then(function () {
            updateCartCount();
            if (btn) {
              btn.textContent = 'Ajouté';
              setTimeout(function () {
                btn.textContent = originalText;
                btn.disabled = false;
              }, 2000);
            }
          })
          .catch(function () {
            if (btn) {
              btn.textContent = originalText;
              btn.disabled = false;
            }
          });
      });
    });
  }

  // ============================================================
  // 12. SPOTLIGHT BORDER — Mouse tracking
  // ============================================================
  function initSpotlight() {
    document.querySelectorAll('[data-spotlight]').forEach(function (el) {
      el.addEventListener('mousemove', function (e) {
        var r = el.getBoundingClientRect();
        el.style.setProperty('--spotlight-x', (e.clientX - r.left) + 'px');
        el.style.setProperty('--spotlight-y', (e.clientY - r.top) + 'px');
      });
      el.addEventListener('mouseleave', function () {
        el.style.setProperty('--spotlight-x', '-999px');
        el.style.setProperty('--spotlight-y', '-999px');
      });
    });
  }

  // ============================================================
  // 13. MAGNETIC BUTTONS
  // ============================================================
  function initMagneticButtons() {
    if (typeof gsap === 'undefined') return;

    // Désactiver sur mobile (pas de hover)
    if (window.matchMedia('(hover: none)').matches) return;

    document.querySelectorAll('.btn-primary:not(.btn-full)').forEach(function (btn) {
      btn.addEventListener('mousemove', function (e) {
        var r = btn.getBoundingClientRect();
        var x = (e.clientX - r.left - r.width / 2) * 0.22;
        var y = (e.clientY - r.top - r.height / 2) * 0.22;
        gsap.to(btn, { x: x, y: y, duration: 0.4, ease: 'power2.out' });
      });

      btn.addEventListener('mouseleave', function () {
        gsap.to(btn, { x: 0, y: 0, duration: 0.6, ease: 'elastic.out(1, 0.5)' });
      });
    });
  }

  // ============================================================
  // 14. GALLERY PARALLAX — Page produit
  // ============================================================
  function initGalleryParallax() {
    if (typeof gsap === 'undefined') return;

    var thumbs = document.querySelectorAll('.product-gallery__thumb');
    if (!thumbs.length) return;

    thumbs.forEach(function (item, i) {
      gsap.to(item, {
        scrollTrigger: {
          trigger: item,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1.2,
        },
        y: i % 2 === 0 ? -16 : 16,
        ease: 'none',
      });
    });
  }

  // ============================================================
  // 15. ANIMATION 3D SCROLL — Boîte MoodScent (scroll-jacked)
  // ============================================================
  function initBoxAnimation() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

    var hero = document.getElementById('home-hero');
    var header = document.getElementById('site-header');
    var logoWrap = document.getElementById('hero-logo-block');
    var scrollIndicator = document.getElementById('hero-scroll-indicator');
    var video = document.getElementById('box-video');
    var slogan = document.getElementById('hero-slogan');

    if (!hero) return;

    // Header caché au départ — apparaît quand le pin se relâche
    if (header) {
      header.style.opacity = '0';
      header.style.pointerEvents = 'none';
      header.style.transition = 'opacity 0.6s ease';
    }

    // Logo fade-in à l'arrivée
    if (logoWrap) {
      gsap.from(logoWrap, { opacity: 0, y: 16, duration: 1.2, ease: 'power2.out', delay: 0.4 });
    }

    // Scroll indicator fade-in
    if (scrollIndicator) {
      gsap.from(scrollIndicator, { opacity: 0, duration: 1, delay: 1.6 });
    }

    // ---- VERSION VIDÉO ----
    if (video && video.querySelector('source')) {
      hero.classList.add('video-active');

      video.addEventListener('loadedmetadata', function() {
        var rafId = null;
        var targetTime = 0;

        ScrollTrigger.create({
          trigger: hero,
          start: 'top top',
          end: '+=600%',   // 6× la hauteur du viewport = durée du pin
          pin: true,
          scrub: 0.5,      // inertie courte = réactif sans être sec
          anticipatePin: 1,
          onUpdate: function(self) {
            // Scrub vidéo — un seul seek par frame via RAF
            targetTime = self.progress * video.duration;
            if (!rafId) {
              rafId = requestAnimationFrame(function() {
                if (video.readyState >= 2) {
                  video.currentTime = targetTime;
                }
                rafId = null;
              });
            }

            // Logo disparaît dans les 15 premiers %
            if (logoWrap) {
              logoWrap.style.opacity = self.progress < 0.15
                ? (1 - self.progress / 0.15).toString()
                : '0';
            }

            // Scroll indicator disparaît dans les 10 premiers %
            if (scrollIndicator) {
              scrollIndicator.style.opacity = self.progress < 0.1
                ? (1 - self.progress / 0.1).toString()
                : '0';
            }

            // Slogan apparaît dans les 20 derniers %
            if (slogan) {
              slogan.style.opacity = self.progress > 0.8
                ? ((self.progress - 0.8) / 0.2).toString()
                : '0';
            }
          },
          onLeave: function() {
            // Pin relâché — header réapparaît
            if (header) {
              header.style.opacity = '1';
              header.style.pointerEvents = '';
            }
          },
          onEnterBack: function() {
            // Si on remonte dans l'animation — cacher le header
            if (header) {
              header.style.opacity = '0';
              header.style.pointerEvents = 'none';
            }
          }
        });
      });

      video.load();

    } else {
      // ---- PAS DE VIDÉO : simple fade-in logo, pas de pin ----
      if (header) {
        header.style.opacity = '1';
        header.style.pointerEvents = '';
      }
    }
  }

  // ============================================================
  // 12. INIT
  // ============================================================
  function init() {
    initHeader();
    initMobileMenu();
    initProductGallery();
    initStickyAtc();
    initNewsletter();
    initAddToCart();
    updateCartCount();

    initBoxAnimation();

    // GSAP après chargement des scripts CDN
    if (typeof gsap !== 'undefined') {
      initGSAP();
    } else {
      // Attendre que GSAP soit disponible
      var attempts = 0;
      var interval = setInterval(function () {
        attempts++;
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
          clearInterval(interval);
          initGSAP();
        }
        if (attempts > 20) clearInterval(interval);
      }, 150);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Fix : retour navigateur (bfcache) sur la homepage → vidéo + ScrollTrigger
  // peuvent être dans un état incohérent. On force un reload propre.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      window.location.reload();
    }
  });
})();
